import { Head, router, useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Vendor {
  id: number
  display_name: string
}

interface Contact {
  id: number
  name: string
}

interface LogForm {
  id: number
  vendor_contact_id: number | null
  log_date: string | null
  notes: string
}

interface Props {
  vendor: Vendor
  log: LogForm
  contacts: Contact[]
  errors?: Record<string, string[]>
}

export default function Edit({ vendor, log, contacts, errors }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Subcontractors", href: "/company/vendors" },
    { title: vendor.display_name, href: `/company/vendors/${vendor.id}` },
    { title: "Logs", href: `/company/vendors/${vendor.id}/logs` },
    {
      title: "Edit Log Entry",
      href: `/company/vendors/${vendor.id}/logs/${log.id}/edit`,
    },
  ]

  const { data, setData, patch, processing, transform } = useForm({
    vendor_contact_id: log.vendor_contact_id ?? "",
    log_date: log.log_date ? log.log_date.slice(0, 10) : "",
    notes: log.notes ?? "",
  })

  transform((data) => ({ vendor_log: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(`/company/vendors/${vendor.id}/logs/${log.id}`)
  }

  const handleDelete = () => {
    if (confirm("Are you sure you want to delete this log entry?")) {
      router.delete(`/company/vendors/${vendor.id}/logs/${log.id}`)
    }
  }

  const inputClass =
    "mt-1 block w-full rounded border border-hpi-light-gray px-3 py-2 focus:border-hpi-active-blue focus:outline-none"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Edit Log Entry" />

      <div className="p-6">
        <h1 className="mb-6 text-2xl font-semibold">Edit Log Entry</h1>

        <form onSubmit={handleSubmit} className="max-w-2xl space-y-4">
          <div>
            <label className={labelClass}>Date</label>
            <input
              type="date"
              value={data.log_date}
              onChange={(e) => setData("log_date", e.target.value)}
              className={inputClass}
            />
            {errors?.log_date && (
              <p className="text-hpi-red mt-1 text-sm">{errors.log_date[0]}</p>
            )}
          </div>

          <div>
            <label className={labelClass}>Contact</label>
            <select
              value={data.vendor_contact_id}
              onChange={(e) => setData("vendor_contact_id", e.target.value)}
              className={inputClass}
            >
              <option value="">-- Select Contact --</option>
              {contacts.map((contact) => (
                <option key={contact.id} value={contact.id}>
                  {contact.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Notes</label>
            <textarea
              value={data.notes}
              onChange={(e) => setData("notes", e.target.value)}
              rows={6}
              className={inputClass}
            />
            {errors?.notes && (
              <p className="text-hpi-red mt-1 text-sm">{errors.notes[0]}</p>
            )}
          </div>

          <div className="flex justify-between">
            <button
              type="button"
              onClick={handleDelete}
              className="bg-hpi-red hover:bg-hpi-red/80 rounded px-4 py-2 text-white"
            >
              Delete
            </button>
            <div className="space-x-4">
              <a
                href={`/company/vendors/${vendor.id}/logs`}
                className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
              >
                Cancel
              </a>
              <button
                type="submit"
                disabled={processing}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
              >
                {processing ? "Saving..." : "Save Log Entry"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
